// The Community tab: CS:S Bunny Hop maps listed live from GameBanana, with search and sorting.
// Each map downloads straight from GameBanana (then the player loads the file here), and shows its
// authors, credits and hiscores.

import {
  BHOP_CATEGORY_URL,
  listMaps,
  mapDetails,
  searchMaps,
  type MapPage,
  type MapSort,
  type MapSummary,
} from '../community/gamebanana';
import { rememberCredit } from '../community/mapCredits';
import { mapBoard } from '../online/mapBoard';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', ...kids: (Node | string)[]) {
  const e = document.createElement(tag);
  if (className) e.className = className;
  e.append(...kids);
  return e;
}

const fmtCount = (n: number) =>
  n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(1) + 'k' : String(n);
const fmtSize = (b: number) =>
  b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB';

export class CommunityTab {
  private readonly root = document.getElementById('community')!;
  private readonly grid = el('ul', 'mapGrid');
  private readonly status = el('p', 'communityStatus');
  private readonly more = el('button', 'secondary', 'Load more maps');
  private readonly search = el('input', '');
  private readonly sort = el('select', '');
  private page = 1;
  private query = '';
  private abort: AbortController | null = null;
  private started = false;
  /** Show a map's hiscores (board key, title). */
  onScores: (board: string, title: string) => void = () => {};

  constructor() {
    this.search.type = 'search';
    this.search.placeholder = 'Search bhop maps by name';
    this.search.setAttribute('aria-label', 'Search bhop maps by name');
    for (const [v, label] of [
      ['downloads', 'Most downloaded'],
      ['likes', 'Most liked'],
      ['views', 'Most viewed'],
      ['newest', 'Newest'],
      ['updated', 'Recently updated'],
    ] as const)
      this.sort.add(new Option(label, v));
    this.sort.setAttribute('aria-label', 'Sort maps');
    const form = el('form', 'communityBar', this.search, this.sort);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.reload();
    });
    let typing = 0;
    this.search.addEventListener('input', () => {
      clearTimeout(typing);
      typing = window.setTimeout(() => this.reload(), 450);
    });
    this.sort.addEventListener('change', () => this.reload());
    this.more.type = 'button';
    this.more.addEventListener('click', () => void this.fetchPage(this.page + 1));

    const head = el(
      'div',
      'communityHead',
      el(
        'h3',
        '',
        'Community bhop maps ',
        el('span', '', 'CS:S · Bunny Hop category · live from GameBanana'),
      ),
    );
    const how = el(
      'ol',
      'communityHow',
      el(
        'li',
        '',
        'Click ',
        el('b', '', 'Download'),
        ' on a map: the file downloads straight from GameBanana.',
      ),
      el('li', '', 'Load that file below (Load a map file), or drop it on this page.'),
      el('li', '', 'It stays in this browser after that, under Your maps.'),
    );
    this.root.append(head, form, how, this.grid, this.status, this.more);
    this.more.hidden = true;
  }

  /** Load the first page (the first time the tab opens). */
  start() {
    if (this.started) return;
    this.started = true;
    this.reload();
  }

  private reload() {
    this.query = this.search.value.trim();
    this.grid.textContent = '';
    void this.fetchPage(1);
  }

  private async fetchPage(page: number) {
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    this.more.hidden = true;
    this.status.textContent = 'Loading maps from GameBanana…';
    let result: MapPage;
    try {
      result = this.query
        ? await searchMaps(this.query, page, abort.signal)
        : await listMaps(this.sort.value as MapSort, page, 18, abort.signal);
    } catch (e) {
      if (abort.signal.aborted) return;
      this.status.textContent = '';
      this.status.append(
        "Couldn't reach GameBanana (" + (e as Error).message + '). You can still browse the ',
        this.link('Bunny Hop category on GameBanana', BHOP_CATEGORY_URL),
        ' and load the files below.',
      );
      return;
    }
    if (abort.signal.aborted) return;
    this.page = page;
    for (const m of result.maps) this.grid.append(this.card(m));
    const shown = this.grid.childElementCount;
    this.status.textContent =
      shown === 0
        ? this.query
          ? `No bhop maps found for “${this.query}”.`
          : 'No maps found.'
        : this.query
          ? `${shown} bhop maps match “${this.query}”.`
          : `${shown} of ${result.total.toLocaleString()} bhop maps.`;
    this.more.hidden = !result.more;
  }

  private link(text: string, href: string) {
    const a = el('a', '', text);
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener';
    return a;
  }

  private card(m: MapSummary) {
    const img = el('img', '');
    if (m.image) {
      img.src = m.image;
      img.loading = 'lazy';
      img.alt = '';
      img.referrerPolicy = 'no-referrer';
    }
    const thumb = el('div', 'mapThumb', m.image ? img : el('span', '', m.name.slice(0, 1).toUpperCase()));
    const name = el('div', 'mapCardName', m.name);
    name.title = m.name;
    const by = el('div', 'mapCardBy', 'by ' + m.submitter);
    const stats = el('div', 'mapCardStats', `♥ ${fmtCount(m.likes)} · ${fmtCount(m.views)} views`);
    // Download straight from GameBanana (its direct file link, /dl/<file id>), no page in between.
    const get = this.link('Download', m.url);
    get.className = 'getMap';
    get.removeAttribute('target');
    get.title = `Download ${m.name} from GameBanana`;
    let direct: string | null = null;
    const page = this.link('Page ↗', m.url);
    page.className = 'mapPage';
    page.title = `Open ${m.name} on GameBanana`;
    const done = el('div', 'mapDownloaded');
    done.hidden = true;
    // Hiscores go by map name; GameBanana titles are usually the map's name (bhop_eazy_v2).
    const scores = el('button', 'mapScores', '🏆');
    scores.hidden = !/^[a-z0-9_.-]+$/i.test(m.name);
    scores.title = `Hiscores of ${m.name}`;
    scores.setAttribute('aria-label', scores.title);
    scores.addEventListener('click', () => this.onScores(mapBoard(m.name), m.name));
    /** Fetch the direct file link (once), and show the file type and size on the button. */
    const prepare = () =>
      mapDetails(m.id).then((d) => {
        const file = d.files.find((x) => /\.(bsp|zip|rar|7z|bz2)$/i.test(x.name)) ?? d.files[0];
        if (!file?.url) return null;
        direct = file.url;
        get.href = file.url;
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        get.textContent = `Download (.${ext}, ${fmtSize(file.size)})`;
        return file.url;
      });
    // Ready before the click (pointing at the card), so the click is a plain download link.
    let preparing: Promise<string | null> | null = null;
    const warm = () => (preparing ??= prepare().catch(() => null));
    const more = el('button', 'link', 'Credits');
    more.type = 'button';
    const details = el('div', 'mapDetails');
    details.hidden = true;

    const remember = (authors: string[], files: string[]) =>
      rememberCredit({ name: m.name, authors: authors.length ? authors : [m.submitter], url: m.url }, files);
    get.addEventListener('pointerenter', warm);
    get.addEventListener('focus', warm);
    get.addEventListener('click', (e) => {
      done.hidden = false;
      done.textContent = 'Downloading… then load the file below (Load a map file) or drop it on this page.';
      if (!direct) {
        // Not ready yet: get the link, then download through a hidden frame (stays on this page).
        e.preventDefault();
        void warm().then((url) => {
          if (!url) {
            done.textContent = "Couldn't get the file: download it on the map's GameBanana page.";
            return;
          }
          const frame = document.createElement('iframe');
          frame.hidden = true;
          frame.src = url;
          document.body.append(frame);
          setTimeout(() => frame.remove(), 60_000);
        });
      }
      remember([m.submitter], []);
      // Fill in the full credits in the background (the file is downloaded on GameBanana).
      mapDetails(m.id)
        .then((d) =>
          remember(
            d.credits.flatMap((c) => c.authors),
            d.files.map((f) => f.name),
          ),
        )
        .catch(() => {});
    });
    more.addEventListener('click', async () => {
      details.hidden = !details.hidden;
      more.textContent = details.hidden ? 'Credits' : 'Hide credits';
      if (details.hidden || details.childElementCount) return;
      details.textContent = 'Loading…';
      try {
        const d = await mapDetails(m.id);
        details.textContent = '';
        for (const c of d.credits)
          details.append(el('div', '', el('b', '', c.group + ': '), c.authors.join(', ')));
        if (!d.credits.length) details.append(el('div', '', el('b', '', 'Submitted by: '), m.submitter));
        const facts = [`${fmtCount(d.downloads)} downloads`];
        if (d.files[0]) facts.push(`${d.files[0].name} (${fmtSize(d.files[0].size)})`);
        details.append(el('div', 'mapFacts', facts.join(' · ')));
        if (d.license) details.append(el('div', 'mapFacts', 'Licence: ' + d.license));
      } catch {
        details.textContent = "Couldn't load the credits. They're on the map's GameBanana page.";
      }
    });
    return el(
      'li',
      'mapCard',
      thumb,
      el(
        'div',
        'mapCardBody',
        name,
        by,
        stats,
        el('div', 'mapCardActions', get, page, scores, more),
        done,
        details,
      ),
    );
  }
}
